import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

const PAGE_ORDER = [
  'login',
  'onboarding',
  'dashboard',
  'study',
  'leaderboard',
  'chat',
  'profile',
  'leader',
  'admin'
];

function getRouteKey(pathname) {
  if (!pathname || pathname === '/' || pathname === '/login') return 'login';
  const segment = pathname.split('/')[1];
  return PAGE_ORDER.includes(segment) ? segment : 'login';
}

export function usePageFlip() {
  const location = useLocation();
  const { role } = useAuthStore();
  const [isFlipping, setIsFlipping] = useState(false);
  const [direction, setDirection] = useState('forward'); // 'forward' | 'backward'
  const [flipProgress, setFlipProgress] = useState(0); // 0 to 1
  const [phase, setPhase] = useState('idle'); // 'idle' | 'lift' | 'curl' | 'land' | 'settle'
  
  // Keeps track of the location we are transition FROM and TO
  const [displayLocation, setDisplayLocation] = useState(location);
  const prevLocationRef = useRef(location);

  // Swipe gesture support
  const touchStart = useRef({ x: 0, y: 0, time: 0 });

  // Handle page navigation tracking
  useEffect(() => {
    const prevPath = prevLocationRef.current.pathname;
    const currPath = location.pathname;

    if (prevPath !== currPath) {
      const prevKey = getRouteKey(prevPath);
      const currKey = getRouteKey(currPath);

      const prevIdx = PAGE_ORDER.indexOf(prevKey);
      const currIdx = PAGE_ORDER.indexOf(currKey);

      const dir = currIdx >= prevIdx ? 'forward' : 'backward';
      setDirection(dir);
      
      triggerFlip(location, dir);
    }
  }, [location]);

  const triggerFlip = (nextLoc, forceDir) => {
    if (isFlipping) {
      // If already flipping, instantly swap and continue
      setDisplayLocation(nextLoc);
      prevLocationRef.current = nextLoc;
      return;
    }

    const dir = forceDir || direction;
    setIsFlipping(true);
    setFlipProgress(0);

    // Sequence timing as specified:
    // Phase 1 - LIFT: 0ms -> 150ms
    setPhase('lift');
    
    // Progress tracker simulation for CSS variables
    let start = null;
    const duration = 900; // Total flip sequence is 900ms

    const animateProgress = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      
      setFlipProgress(progress);

      if (elapsed < duration) {
        requestAnimationFrame(animateProgress);
      }
    };
    requestAnimationFrame(animateProgress);

    // Phase 2 - CURL: 150ms -> 600ms
    const curlTimeout = setTimeout(() => {
      setPhase('curl');
      // Halfway through Phase 2 (around 375ms), the old page content transitions
      // and we swap the background content underneath so the new page is revealed.
      setDisplayLocation(nextLoc);
    }, 150);

    // Phase 3 - LAND: 600ms -> 800ms
    const landTimeout = setTimeout(() => {
      setPhase('land');
    }, 600);

    // Phase 4 - SETTLE: 800ms -> 900ms
    const settleTimeout = setTimeout(() => {
      setPhase('settle');
    }, 800);

    // End flip and settle: 900ms
    const endTimeout = setTimeout(() => {
      setPhase('idle');
      setIsFlipping(false);
      setFlipProgress(0);
      prevLocationRef.current = nextLoc;
    }, 900);

    return () => {
      clearTimeout(curlTimeout);
      clearTimeout(landTimeout);
      clearTimeout(settleTimeout);
      clearTimeout(endTimeout);
    };
  };

  // Swipe gesture listeners for mobile
  const handleTouchStart = (e) => {
    if (window.innerWidth >= 768) return; // Only mobile
    const touch = e.touches[0];
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  };

  const handleTouchEnd = (e, navigate) => {
    if (role === 'admin') return;
    if (window.innerWidth >= 768 || !navigate) return;
    const touch = e.changedTouches[0];
    const diffX = touch.clientX - touchStart.current.x;
    const diffY = touch.clientY - touchStart.current.y;
    const diffTime = Date.now() - touchStart.current.time;

    // Minimum swipe distance: 60px, within a reasonable time (300ms)
    if (Math.abs(diffX) > 60 && diffTime < 300) {
      const prevKey = getRouteKey(location.pathname);
      const prevIdx = PAGE_ORDER.indexOf(prevKey);

      if (diffX < 0) {
        // Swipe left -> Navigate forward (standard book turn)
        if (prevIdx < PAGE_ORDER.length - 1) {
          const nextRoute = PAGE_ORDER[prevIdx + 1];
          navigate(`/${nextRoute}`);
        }
      } else {
        // Swipe right -> Navigate backward
        if (prevIdx > 0) {
          const nextRoute = PAGE_ORDER[prevIdx - 1];
          navigate(`/${nextRoute}`);
        }
      }
    }
  };

  return {
    isFlipping,
    direction,
    flipProgress,
    phase,
    displayLocation,
    triggerFlip,
    handleTouchStart,
    handleTouchEnd
  };
}
